import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@veracity/vui'
import { useNavigate } from 'react-router-dom'

import { portfoliosClient, projectsClient } from '@/api'
import { UpdateLinkedProjectsRequest } from '@/interfaces'

const BASE_QUERY_KEY = portfoliosClient.QUERY_KEY
const BASE_ROUTE = '/portfolios'
export const usePortfolioDetails = (portfolioId: number) => {
  const QUERY_KEY = [...BASE_QUERY_KEY, portfolioId]
  const navigate = useNavigate()
  const { showSuccess } = useToast()
  const queryClient = useQueryClient()

  const getPortfolioDetailsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => portfoliosClient.getById(portfolioId),
    enabled: !!portfolioId
  })

  const getAllProjectsQuery = useQuery({
    queryKey: projectsClient.QUERY_KEY,
    queryFn: projectsClient.getAll
  })

  const linkProjectsMutation = useMutation({
    mutationFn: (req: UpdateLinkedProjectsRequest) =>
      portfoliosClient.updateLinkedProjects(portfolioId, req),
    onSuccess: async () => {
      showSuccess('Portfolio successfully updated')
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY })
    }
  })

  const removePortfolioMutation = useMutation({
    mutationFn: () => portfoliosClient.removeById(portfolioId),
    onSuccess: () => {
      showSuccess('Portfolio successfully removed')
      navigate(BASE_ROUTE)
    }
  })

  const linkedProjectsIds = getPortfolioDetailsQuery.data?.projects?.map(
    x => x.id
  )

  return {
    portfolioDetails: getPortfolioDetailsQuery.data,
    isFetching: getPortfolioDetailsQuery.isFetching,
    linkProjects: linkProjectsMutation.mutate,
    isLinkProjectsInProgress: linkProjectsMutation.isPending,
    linkedProjectsIds: linkedProjectsIds || [],
    allProjects: getAllProjectsQuery.data || [],
    removePortfolio: removePortfolioMutation.mutate,
    isRemovePortfolioInProgress: removePortfolioMutation.isPending
  }
}
